import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, BellRing, CalendarDays, Download, ExternalLink, Link2, Loader2, Lock, LockOpen, Mail, MapPin, MessageCircle, Phone, RefreshCw, Search, Send, Users } from 'lucide-react';
import DeleteConfirmModal from '@/components/admin/DeleteConfirmModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const csvEscape = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v).replace(/"/g, '""').replace(/\r?\n/g, ' ');
  return /[",;\n]/.test(s) ? `"${s}"` : s;
};

const EMAIL_STATUS = {
  sent: { label: 'Email inviata', cls: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  failed: { label: 'Email fallita', cls: 'bg-red-50 border-red-200 text-red-600' },
  pending: { label: 'Email in coda', cls: 'bg-slate-50 border-slate-200 text-slate-500' },
};

const ENRICH_STATUS = {
  pending_manual_research: { label: 'Ricerca manuale richiesta', cls: 'bg-amber-50 border-amber-300 text-amber-700' },
  queued: { label: 'Enrichment in coda', cls: 'bg-sky-50 border-sky-200 text-sky-700' },
  enriched: { label: 'Arricchito · Google CSE', cls: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  verified: { label: 'Verificato', cls: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  not_found: { label: 'Nessuna fonte pubblica', cls: 'bg-slate-50 border-slate-200 text-slate-500' },
  error: { label: 'Errore ricerca', cls: 'bg-red-50 border-red-200 text-red-600' },
};

const SyncChip = ({ ok, okLabel, koLabel, testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[10.5px] font-bold ${
      ok ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-400'
    }`}
  >
    {ok ? '✓' : '—'} {ok ? okLabel : koLabel}
  </span>
);

const waLink = (r, link) => {
  const phone = String(r.phone || '').replace(/[^\d]/g, '');
  const msg = `Ciao ${r.first_name || ''}! Promemoria: Webinar Privacy Sanitaria per Oculisti — 10 ottobre 2026, ore 10:30 (ora italiana, Roma). Link per collegarti: ${link} — Prime Vision`;
  return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
};

export const WebinarPanel = ({ token }) => {
  const [data, setData] = useState(null);
  const [reminder, setReminder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [search, setSearch] = useState('');
  const [linkInput, setLinkInput] = useState('');
  const [savingLink, setSavingLink] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const headers = useMemo(() => ({ 'X-Admin-Token': token }), [token]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [regs, rem] = await Promise.all([
        axios.get(`${API}/admin/webinar/registrations`, { headers }),
        axios.get(`${API}/admin/webinar/reminder-status`, { headers }),
      ]);
      setData(regs.data);
      setReminder(rem.data);
      setLinkInput(rem.data?.access_link || '');
    } catch {
      toast.error('Errore nel caricamento iscritti webinar');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggle = async () => {
    if (!data) return;
    setToggling(true);
    const next = !data.registrations_open;
    try {
      await axios.post(`${API}/admin/webinar/toggle`, { open: next }, { headers });
      toast.success(next ? 'Iscrizioni APERTE' : 'Iscrizioni CHIUSE');
      await fetchAll();
    } catch {
      toast.error('Errore nel cambio stato iscrizioni');
    } finally {
      setToggling(false);
    }
  };

  const saveLink = async () => {
    setSavingLink(true);
    try {
      await axios.post(`${API}/admin/webinar/access-link`, { link: linkInput.trim() }, { headers });
      toast.success(linkInput.trim() ? 'Link di accesso salvato' : 'Link di accesso rimosso');
      await fetchAll();
    } catch (err) {
      if (err?.response?.status === 400) toast.error('Link non valido: deve iniziare con http:// o https://');
      else toast.error('Salvataggio link fallito');
    } finally {
      setSavingLink(false);
    }
  };

  const sendReminderNow = async () => {
    setSendingReminder(true);
    setConfirmSend(false);
    try {
      const r = await axios.post(`${API}/admin/webinar/send-reminder`, { force: false }, { headers });
      toast.success(`Reminder inviato: ${r.data.sent} ok · ${r.data.failed} falliti · ${r.data.skipped} già notificati`);
      await fetchAll();
    } catch (err) {
      if (err?.response?.status === 409) toast.error('Imposta prima il link di accesso');
      else toast.error('Invio reminder fallito');
    } finally {
      setSendingReminder(false);
    }
  };

  const regs = useMemo(() => data?.registrations || [], [data]);

  const filtered = useMemo(() => {
    if (!search.trim()) return regs;
    const q = search.toLowerCase();
    return regs.filter((r) =>
      [r.first_name, r.last_name, r.email, r.phone, r.city]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [regs, search]);

  const exportCSV = () => {
    if (filtered.length === 0) {
      toast.warning('Nessun iscritto da esportare');
      return;
    }
    const keys = ['first_name', 'last_name', 'email', 'phone', 'city', 'privacy_consent', 'newsletter_consent', 'whatsapp_consent', 'email_status', 'reminder_email_status', 'crm_synced', 'crm_contact_id', 'crm_action', 'newsletter_synced', 'whatsapp_synced', 'enrichment_status', 'enrichment_query', 'workplace_name', 'studio_name', 'role', 'website', 'work_email', 'work_phone', 'workplace_address', 'enrichment_source_url', 'locale', 'source', 'event_id', 'created_at'];
    const rows = filtered.map((r) => ({ ...(r.crm_professional || {}), ...r }));
    const header = keys.map(csvEscape).join(',');
    const body = rows.map((r) => keys.map((k) => csvEscape(r[k])).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + `${header}\n${body}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `prime-vision-webinar-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Esportati ${filtered.length} iscritti`);
  };

  const sentCount = regs.filter((r) => r.email_status === 'sent').length;
  const failedCount = regs.filter((r) => r.email_status === 'failed').length;
  const accessLink = reminder?.access_link || '';

  return (
    <div className="space-y-6" data-testid="pv-admin-webinar-panel">
      {/* Event header + toggle */}
      <div className="bg-gradient-to-br from-violet-50 to-white border border-violet-200 rounded-2xl p-5 md:p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className="shrink-0 w-11 h-11 rounded-xl bg-white border border-violet-300 flex items-center justify-center">
              <CalendarDays className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-violet-700 font-bold mb-1">
                Evento · {data?.event_id || '—'}
              </div>
              <h3 className="font-heading font-extrabold text-pv-ink text-[17px] md:text-[18px] leading-tight">
                {data?.title || 'Webinar Privacy Sanitaria per Oculisti'}
              </h3>
              <p className="text-[12.5px] text-slate-600 mt-1">
                {data?.date_label || '10 ottobre 2026'} · {data?.time_label || 'Ore 10:30 AM · ora italiana (Roma)'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={fetchAll}
              disabled={loading}
              className="pv-btn-ghost !py-2 !px-4 !text-[12px]"
              data-testid="pv-admin-webinar-refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Aggiorna
            </button>
            <button
              onClick={toggle}
              disabled={toggling || !data}
              data-testid="pv-admin-webinar-toggle"
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[12.5px] font-bold transition-colors border ${
                data?.registrations_open
                  ? 'bg-white border-red-300 text-red-700 hover:bg-red-50'
                  : 'bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700'
              } disabled:opacity-50`}
            >
              {toggling ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : data?.registrations_open ? (
                <Lock className="w-3.5 h-3.5" />
              ) : (
                <LockOpen className="w-3.5 h-3.5" />
              )}
              {data?.registrations_open ? 'Chiudi iscrizioni' : 'Apri iscrizioni'}
            </button>
          </div>
        </div>
        <div className="mt-3">
          <span
            data-testid="pv-admin-webinar-status"
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.14em] border ${
              data?.registrations_open
                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                : 'bg-red-50 border-red-200 text-red-600'
            }`}
          >
            {data?.registrations_open ? '● Iscrizioni aperte' : '● Iscrizioni chiuse'}
          </span>
        </div>
      </div>

      {/* Access link + 24h reminder */}
      <div className="bg-white border border-orange-200 rounded-2xl p-5 md:p-6" data-testid="pv-admin-webinar-reminder-card">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="shrink-0 w-11 h-11 rounded-xl bg-orange-50 border border-orange-200 flex items-center justify-center">
            <BellRing className="w-5 h-5 text-orange-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-orange-600 font-bold mb-1">
              Link di accesso · promemoria 24h
            </div>
            <h3 className="font-heading font-bold text-pv-ink text-[16px] leading-tight">
              Il reminder parte automaticamente il {reminder?.reminder_label || '9 ottobre 2026 · ore 10:30 (Roma)'}
            </h3>
            <p className="text-[12.5px] text-slate-600 mt-1 leading-snug max-w-2xl">
              Incolla qui il link del webinar appena lo hai: verrà inviato via email a tutti gli iscritti 24 ore prima
              dell&apos;evento. WhatsApp: {reminder?.whatsapp_mode === 'provider'
                ? 'invio automatico attivo.'
                : 'modalità manuale — usa il pulsante WhatsApp verde su ogni iscritto (messaggio precompilato con il link).'}
            </p>
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <input
                type="url"
                value={linkInput}
                onChange={(e) => setLinkInput(e.target.value)}
                placeholder="https://… link di accesso al webinar"
                data-testid="pv-admin-webinar-link-input"
                className="pv-input flex-1 !py-2.5 !text-[13px]"
                disabled={savingLink}
              />
              <button
                onClick={saveLink}
                disabled={savingLink || (linkInput.trim() === accessLink)}
                data-testid="pv-admin-webinar-link-save"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-pv-teal hover:bg-sky-700 text-white text-[13px] font-bold transition-colors disabled:opacity-40 shrink-0"
              >
                {savingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                Salva link
              </button>
            </div>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span
                data-testid="pv-admin-webinar-link-status"
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold border ${
                  reminder?.access_link_set
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                    : 'bg-amber-50 border-amber-300 text-amber-700'
                }`}
              >
                {reminder?.access_link_set ? '✓ Link impostato' : '⚠ Link mancante — il reminder non partirà'}
              </span>
              {reminder && (
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-600 text-[11px] font-bold" data-testid="pv-admin-webinar-reminder-counts">
                  Reminder: {reminder.reminder_sent} inviati · {reminder.reminder_failed} falliti · {reminder.registrants_total} iscritti
                </span>
              )}
              {reminder?.reminder_auto_done && (
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-bold">
                  ✓ Invio automatico completato
                </span>
              )}
            </div>
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              {!confirmSend ? (
                <button
                  onClick={() => setConfirmSend(true)}
                  disabled={sendingReminder || !reminder?.access_link_set || (reminder?.registrants_total || 0) === 0}
                  data-testid="pv-admin-webinar-reminder-send"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border border-orange-300 text-orange-600 hover:bg-orange-50 text-[13px] font-bold transition-colors disabled:opacity-40"
                >
                  <Send className="w-4 h-4" />
                  Invia reminder ora
                </button>
              ) : (
                <>
                  <span className="text-[12.5px] font-bold text-slate-700">
                    Confermi l&apos;invio a {reminder?.registrants_total || 0} iscritti? (chi ha già ricevuto viene saltato)
                  </span>
                  <button
                    onClick={sendReminderNow}
                    disabled={sendingReminder}
                    data-testid="pv-admin-webinar-reminder-confirm"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-[12.5px] font-bold transition-colors"
                  >
                    {sendingReminder ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    Sì, invia adesso
                  </button>
                  <button
                    onClick={() => setConfirmSend(false)}
                    className="pv-btn-ghost !py-2 !px-3 !text-[12px]"
                    data-testid="pv-admin-webinar-reminder-cancel"
                  >
                    Annulla
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Enrichment missing-key notice */}
      {data && data.enrichment_search_configured === false && (
        <div
          className="bg-amber-50 border border-amber-300 rounded-2xl p-4 flex items-start gap-3"
          data-testid="pv-admin-webinar-enrichment-banner"
        >
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-bold text-[13px] text-amber-900">
              Arricchimento dati professionali · manca la chiave di ricerca
            </div>
            <p className="text-[12.5px] text-amber-800 mt-1 leading-snug">
              Nessuna API key configurata (<code className="font-mono text-[11px]">{data.enrichment_key_env || 'ENRICHMENT_SEARCH_API_KEY'}</code>):
              i nuovi iscritti vengono marcati <strong>pending_manual_research</strong> con una query di ricerca suggerita
              (nome + cognome + città). Aggiungi la chiave in <code className="font-mono text-[11px]">backend/.env</code> per attivare l&apos;enrichment automatico.
            </p>
          </div>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-violet-200 p-5" data-testid="pv-admin-webinar-count">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">
            <Users className="w-3.5 h-3.5 text-violet-600" /> Iscritti totali
          </div>
          <div className="font-heading font-extrabold text-4xl text-pv-ink">{data?.count ?? '—'}</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-600 font-bold mb-2">
            <Mail className="w-3.5 h-3.5" /> Email inviate
          </div>
          <div className="font-heading font-extrabold text-4xl text-emerald-600">{data ? sentCount : '—'}</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-red-600 font-bold mb-2">
            <Mail className="w-3.5 h-3.5" /> Email fallite
          </div>
          <div className="font-heading font-extrabold text-4xl text-red-600">{data ? failedCount : '—'}</div>
        </div>
      </div>

      {/* Search + export */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca nome, email, città…"
            data-testid="pv-admin-webinar-search"
            className="pl-9 pr-3 py-2 rounded-full bg-white border border-slate-200 text-[13px] text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-violet-400 transition-colors w-64"
          />
        </div>
        <button
          onClick={exportCSV}
          data-testid="pv-admin-webinar-export"
          className="pv-btn-primary !py-2 !px-4 !text-[12.5px]"
        >
          <Download className="w-3.5 h-3.5" />
          Esporta CSV
        </button>
      </div>

      {/* Registrations list */}
      {loading && regs.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 text-violet-600 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-200">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-slate-100 text-slate-400 mb-3">
            <Users className="w-5 h-5" />
          </div>
          <p className="text-slate-500 font-semibold">Nessun iscritto ancora</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => {
            const status = EMAIL_STATUS[r.email_status] || EMAIL_STATUS.pending;
            const fullName = `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.email;
            return (
              <article
                key={r.id}
                data-testid={`pv-admin-webinar-reg-${r.id}`}
                className="bg-white rounded-2xl border border-slate-200 p-5 hover:border-violet-300 transition-colors"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-11 h-11 rounded-xl bg-violet-50 border border-violet-200 flex items-center justify-center shrink-0">
                      <span className="font-heading font-extrabold text-violet-600 text-sm">
                        {(r.first_name?.[0] || '?').toUpperCase()}{(r.last_name?.[0] || '').toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="font-heading font-extrabold text-pv-ink text-base truncate">{fullName}</div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 font-mono uppercase tracking-wider flex-wrap">
                        <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" />{formatDate(r.created_at)}</span>
                        {r.city && <span className="inline-flex items-center gap-1">· <MapPin className="w-3 h-3" />{r.city}</span>}
                        {r.locale && <span>· {r.locale.toUpperCase()}</span>}
                        {r.source && <span>· {r.source}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] font-bold ${status.cls}`}>
                      <Mail className="w-3 h-3" /> {status.label}
                    </span>
                    {r.reminder_email_status && (
                      <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full border text-[11px] font-bold ${
                        r.reminder_email_status === 'sent'
                          ? 'bg-orange-50 border-orange-200 text-orange-600'
                          : 'bg-red-50 border-red-200 text-red-600'
                      }`}>
                        <BellRing className="w-3 h-3" /> {r.reminder_email_status === 'sent' ? 'Reminder ✓' : 'Reminder ✕'}
                      </span>
                    )}
                    {r.newsletter_consent && (
                      <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-violet-50 border border-violet-200 text-violet-700 text-[11px] font-bold">
                        Newsletter ✓
                      </span>
                    )}
                    <button
                      onClick={() => setDeleteTarget({
                        collection: 'webinar_registrations',
                        collectionLabel: 'Webinar',
                        id: r.id,
                        label: `${fullName} · ${r.email}`,
                      })}
                      data-testid={`pv-admin-webinar-delete-${r.id}`}
                      title="Elimina definitivamente (richiede conferma)"
                      className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white border border-slate-200 text-slate-300 hover:text-red-600 hover:border-red-300 hover:bg-red-50 transition-colors shrink-0"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
                  <a
                    href={`mailto:${r.email}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-slate-700 text-[12px] font-bold hover:border-violet-300 hover:text-violet-600 transition-colors"
                  >
                    <Mail className="w-3 h-3" /> {r.email}
                  </a>
                  {r.phone && (
                    <a
                      href={`tel:${r.phone}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-slate-700 text-[12px] font-bold hover:border-violet-300 hover:text-violet-600 transition-colors"
                    >
                      <Phone className="w-3 h-3" /> {r.phone}
                    </a>
                  )}
                  {r.phone && accessLink && (
                    <a
                      href={waLink(r, accessLink)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`pv-admin-webinar-wa-${r.id}`}
                      title="Invia il promemoria WhatsApp (messaggio precompilato)"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-700 text-[12px] font-bold hover:bg-emerald-100 transition-colors"
                    >
                      <MessageCircle className="w-3 h-3" /> WhatsApp reminder
                    </a>
                  )}
                </div>

                {/* CRM sync + enrichment status */}
                <div className="mt-2.5 flex items-center gap-2 flex-wrap" data-testid={`pv-admin-webinar-sync-${r.id}`}>
                  <SyncChip
                    ok={!!r.crm_synced}
                    okLabel={`CRM${r.crm_action === 'created' ? ' · creato' : r.crm_action === 'updated' ? ' · aggiornato' : ''}`}
                    koLabel="CRM non sincronizzato"
                  />
                  <SyncChip ok={!!r.newsletter_synced} okLabel="Newsletter" koLabel="Newsletter" />
                  <SyncChip ok={!!r.whatsapp_synced} okLabel="WhatsApp" koLabel="WhatsApp" />
                  {(() => {
                    const es = ENRICH_STATUS[r.enrichment_status || r.crm_professional?.enrichment_status];
                    const query = r.crm_professional?.enrichment_query;
                    return (
                      <span
                        title={query ? `Query suggerita: ${query}` : undefined}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[10.5px] font-bold ${
                          es ? es.cls : 'bg-slate-50 border-slate-200 text-slate-400'
                        }`}
                      >
                        {es ? es.label : 'Enrichment —'}
                      </span>
                    );
                  })()}
                  {r.crm_contact_id && (
                    <span
                      title={r.crm_contact_id}
                      className="inline-flex items-center px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-500 text-[10.5px] font-mono font-bold"
                    >
                      CRM ID: {r.crm_contact_id.slice(0, 8)}…
                    </span>
                  )}
                  {(r.crm_professional?.enrichment_source_url || r.crm_professional?.source_url) && (
                    <a
                      href={r.crm_professional.enrichment_source_url || r.crm_professional.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-sky-50 border border-sky-200 text-sky-700 text-[10.5px] font-bold hover:bg-sky-100 transition-colors"
                    >
                      <ExternalLink className="w-3 h-3" /> Fonte
                    </a>
                  )}
                  {(r.crm_professional?.workplace_name || r.crm_professional?.studio_name) && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-violet-50 border border-violet-200 text-violet-700 text-[10.5px] font-bold">
                      {r.crm_professional.workplace_name || r.crm_professional.studio_name}
                      {r.crm_professional.role ? ` · ${r.crm_professional.role}` : ''}
                    </span>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      <DeleteConfirmModal
        target={deleteTarget}
        token={token}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => { setDeleteTarget(null); fetchAll(); }}
      />
    </div>
  );
};

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
  </svg>
);

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

export default WebinarPanel;
